import isEmpty from 'lodash/isEmpty'
import { useMemo, useState } from 'react'
import { Table } from '@ztechapps/webui.data-tools'
import { SearchInput } from '@ztechapps/webui.input'
import { EmptyContent } from '@ztechapps/webui.util'
import { useLocalListAction } from '@ztechapps/common.hook'
import type { PermissionCatalog, Sort } from '@ztechapps/common.contract'
import { PermissionListHeader, type SortColumn } from './PermissionListHeader'
import { PermissionListRow } from './PermissionListRow'
import styles from './PermissionList.module.scss'

export type PermissionListProps = {
  catalog: PermissionCatalog
  hasPermission: (value: string) => boolean
  onToggle: (args: { value: string; set: boolean }) => Promise<void> | void
}

export enum PermissionSortKey {
  Value = 'value',
  Description = 'description',
  Group = 'group',
  Checked = 'checked',
}

export type PermissionRow = {
  value: string
  description: string
  group: string
  checked: boolean
}

// The module's `label` becomes the row's Group — the matrix's section heading collapses into a
// plain sortable column, so a permission is always exactly one row (no Module × action grid).
const flattenCatalog = (catalog: PermissionCatalog, hasPermission: (value: string) => boolean): PermissionRow[] =>
  catalog.flatMap((module) =>
    module.permissions.map((permission) => ({
      value: permission.value,
      description: permission.description,
      group: module.label,
      checked: hasPermission(permission.value),
    }))
  )

// Case-insensitive multi-field `$or` regex — mirrors the search-config shape
// `handleSearchFn` builds per-field in libs/webui/data-tools/src/DataTool/SearchInput/useMongoSearch.tsx,
// scoped down to this table's three searchable columns since there's no DataTool/ListField here.
const buildPermissionSearchObject = (searchValue: string): Record<string, unknown> => {
  const trimmed = searchValue.trim()
  if (isEmpty(trimmed)) return {}
  const regex = { $regex: trimmed, $options: 'i' }
  return { $or: [{ value: regex }, { description: regex }, { group: regex }] }
}

const SORT_COLUMNS: SortColumn[] = [
  { key: PermissionSortKey.Value, label: 'Permission' },
  { key: PermissionSortKey.Description, label: 'Description' },
  { key: PermissionSortKey.Group, label: 'Group' },
  { key: PermissionSortKey.Checked, label: 'Granted' },
]

/**
 * Renders every permission in the catalog as a flat, sortable table row — one row per
 * permission, four columns (Permission/Description/Group/Granted). There is no matrix column
 * and no secondary/indented row, so a permission missing one of the legacy
 * canAccess/canView/canEdit/canDelete keys is never structurally ungrantable.
 */
export const PermissionList = ({ catalog, hasPermission, onToggle }: PermissionListProps) => {
  const [searchValue, setSearchValue] = useState('')

  const allRows = useMemo(() => flattenCatalog(catalog, hasPermission), [catalog, hasPermission])

  // No `defaultSort` — the catalog's own module/permission order is the initial row order
  // (some scenarios assert against it), so sorting only kicks in once a header is clicked.
  const { list: rows, onSort, onFilter, sort } = useLocalListAction<PermissionRow>(allRows, { defaultSort: null })

  // `sort` is a `{ [field]: direction }` map (or `null` before any header click) — derive the
  // single active column/direction pair the header needs for its arrow indicator.
  const [sortKey, sortDir] = (Object.entries(sort ?? {})[0] ?? []) as [PermissionSortKey | undefined, 1 | -1 | undefined]

  const toggleSort = (key: PermissionSortKey) => {
    const nextDir: 1 | -1 = sortKey === key && sortDir === 1 ? -1 : 1
    onSort({ sortValue: { name: key, direction: nextDir }, sortObject: { sort: { [key]: nextDir } as Sort<PermissionRow> } })
  }

  const onSearch = (value: string) => {
    setSearchValue(value)
    onFilter({ searchValue: value, searchObject: buildPermissionSearchObject(value) })
  }

  // `Table` spreads each `dataArray` entry onto the row component, so `onToggle` rides
  // along as just another prop rather than being threaded through row-render callbacks.
  const rowsWithToggle = useMemo(() => rows.map((row) => ({ ...row, onToggle })), [rows, onToggle])

  return (
    <div>
      <SearchInput value={searchValue} onSearch={onSearch} placeholder="Search permissions" />
      {isEmpty(rows) ? (
        <EmptyContent message="No permissions match your search." />
      ) : (
        <div className={`${styles.tableWrap} mt-3`}>
          <Table
            dataArray={rowsWithToggle}
            idFieldName="value"
            row={PermissionListRow}
            tableClassName="table table-hover table-sm mb-0"
            head={
              <PermissionListHeader
                columns={SORT_COLUMNS}
                sortKey={sortKey}
                sortDir={sortDir ?? 1}
                onToggleSort={toggleSort}
                checkedColumnKey={PermissionSortKey.Checked}
              />
            }
          />
        </div>
      )}
    </div>
  )
}
